import { apiService } from '../../../services/apiService';
import {
  API_PATHS,
  getAcquirerTerminalSoftDeletePath,
  getMerchantPriorityRoutingPath,
  getMerchantTerminalSoftDeletePath,
  getTerminalMappingSoftDeletePath,
} from '../../../utils/apiPaths';
import { createCompleteGridFetcher } from '../../../utils/fetchCompleteGridList';
import type { TerminalMappingListRequest, TerminalsColumnFilters } from '../models/types';
import {
  buildAcquirerTerminalsRequest,
  buildMappingsRequest,
  buildMerchantTerminalsRequest,
  extractApiList,
  mapAcquirerTerminalRow,
  mapMerchantTerminalRow,
  mapTerminalRow,
  normalizeCountryLookups,
} from '../utils/utils';

function readTotalElements(data: unknown, fallback: number): number {
  if (!data || typeof data !== 'object') return fallback;
  const record = data as { totalElements?: unknown; page?: { totalElements?: unknown } };
  const page =
    record.page && typeof record.page === 'object' && !Array.isArray(record.page)
      ? record.page
      : null;
  const total = Number(record.totalElements ?? page?.totalElements ?? fallback);
  return Number.isFinite(total) ? total : fallback;
}

export async function fetchTerminalMappings(payload: TerminalMappingListRequest) {
  const res: any = await apiService.post(API_PATHS.GET_MERCHANT_ACQUIRER_MAPPINGS, payload);
  if (!res?.success) {
    throw new Error('Failed to load terminals.');
  }
  const content = res.data?.content ?? [];
  const rows = Array.isArray(content)
    ? content.map((item: Record<string, unknown>) => mapTerminalRow(item))
    : [];
  return {
    rows,
    totalElements: readTotalElements(res.data, rows.length),
  };
}

export const fetchTerminalMappingsGrid = createCompleteGridFetcher((
  options: Parameters<typeof buildMappingsRequest>[0],
) => fetchTerminalMappings(buildMappingsRequest(options)));

export async function fetchMerchantTerminalsList(options: {
  search: string;
  columnFilters: TerminalsColumnFilters;
  countryIdsByCurrency?: Record<string, number[]>;
  page?: number;
  size?: number;
  export?: boolean;
}) {
  const res: any = await apiService.post(
    API_PATHS.GET_ALL_MERCHANT_TERMINALS,
    buildMerchantTerminalsRequest(options),
  );

  if (!res?.success) {
    throw new Error(res?.message || 'Failed to load merchant terminals.');
  }

  const content = extractApiList(res);
  const rows = content.map(mapMerchantTerminalRow).filter((row) => row.terminalId > 0);
  return {
    rows,
    totalElements: readTotalElements(res.data, rows.length),
  };
}

export const fetchMerchantTerminalsGrid = createCompleteGridFetcher(fetchMerchantTerminalsList);

export async function fetchAcquirerTerminalsList(options: {
  search: string;
  columnFilters: TerminalsColumnFilters;
  countryIdsByCurrency?: Record<string, number[]>;
  page?: number;
  size?: number;
  export?: boolean;
}) {
  const res: any = await apiService.post(
    API_PATHS.GET_ALL_ACQUIRER_TERMINALS,
    buildAcquirerTerminalsRequest(options),
  );

  if (!res?.success) {
    throw new Error(res?.message || 'Failed to load acquirer terminals.');
  }

  const content = extractApiList(res);
  const rows = content.map(mapAcquirerTerminalRow).filter((row) => row.terminalId > 0);
  return {
    rows,
    totalElements: readTotalElements(res.data, rows.length),
  };
}

export const fetchAcquirerTerminalsGrid = createCompleteGridFetcher(fetchAcquirerTerminalsList);

export async function fetchCountryLookups() {
  const res: any = await apiService.get(API_PATHS.GET_ACTIVE_COUNTRIES);
  const raw = res?.data ?? res;
  return normalizeCountryLookups(raw);
}

export async function fetchActiveAcquirers() {
  const res: any = await apiService.post(API_PATHS.GET_ACQUIRERS_BY_STATUS, { status: 'ACTIVE' });
  if (!res?.success) {
    throw new Error('Could not load acquirers.');
  }
  const pageData = res?.data?.content ? res.data : res;
  const rows = pageData.content || [];
  return rows
    .map((a: any) => ({ id: Number(a.id), name: String(a.name || `Acquirer ${a.id}`) }))
    .filter((a: { id: number }) => a.id > 0);
}

export async function fetchActiveMerchants() {
  const res: any = await apiService.post(API_PATHS.GET_MERCHANTS, {
    page: 0,
    size: 100,
    status: 'ACTIVE',
  });
  if (!res?.success) {
    throw new Error('Could not load merchants.');
  }
  const pageData = res?.data?.content ? res.data : res;
  const rows = pageData.content || [];
  return rows
    .map((m: any) => ({
      id: Number(m.id),
      name: String(m.name || `Merchant ${m.id}`),
      priorityRoutingEnabled: Boolean(m.priorityRoutingEnabled),
    }))
    .filter((m: { id: number }) => m.id > 0);
}

export async function fetchAcquirerTerminalsByAcquirerId(acquirerId: number) {
  const res: any = await apiService.post(API_PATHS.GET_ACQUIRER_TERMINALS, { acquirerId });
  const list = Array.isArray(res?.data?.content ?? res?.data)
    ? (res?.data?.content ?? res?.data)
    : [];
  return list
    .map((t: any) => ({
      id: Number(t.id ?? t.autoId ?? t.terminalId ?? 0),
      name: String(t.name ?? t.terminalName ?? `Terminal ${t.id ?? ''}`),
      currency: t.currency ? String(t.currency) : undefined,
    }))
    .filter((t: { id: number }) => t.id > 0);
}

export async function fetchMerchantTerminalsByMerchantId(merchantId: number) {
  const res: any = await apiService.post(API_PATHS.GET_MERCHANT_TERMINALS, { merchantId });
  const list = Array.isArray(res?.data?.content ?? res?.data)
    ? (res?.data?.content ?? res?.data)
    : [];
  return list
    .map((t: any) => ({
      id: Number(t.id ?? t.autoId ?? t.terminalId ?? 0),
      name: String(t.name ?? t.terminalName ?? `Terminal ${t.id ?? ''}`),
    }))
    .filter((t: { id: number; name: string }) => t.id > 0);
}

export async function createTerminalMapping(payload: {
  merchantId: number;
  merchantTerminalId: number;
  acquirerId: number;
  acquirerTerminalId: number;
  /** Required (1, 2, or 3) only when the merchant has priorityRoutingEnabled true. */
  priority?: number;
  /** When true, the hosted checkout page collects the shopper's billing details instead of
   * requiring the merchant to send them in processPayment for this mapping. */
  collectCustomerDetailsOnCheckout?: boolean;
  /** Collect Group only, dev only: skip tax ID / DOB and send placeholders. */
  testDummyValues?: boolean;
  /** Collect Group only: the APM scheme slugs to offer on checkout for this mapping. */
  apmSchemes?: string[];
}) {
  return apiService.post(API_PATHS.ADD_TERMINAL_MAPPING, payload);
}

export async function updateTerminalMapping(payload: {
  mappingId: number;
  merchantId: number;
  acquirerId: number;
  merchantTerminalId: number;
  acquirerTerminalId: number;
  status: string;
  /** Required (1, 2, or 3) only when the merchant has priorityRoutingEnabled true. */
  priority?: number;
  /** When true, the hosted checkout page collects the shopper's billing details instead of
   * requiring the merchant to send them in processPayment for this mapping. */
  collectCustomerDetailsOnCheckout?: boolean;
  /** Collect Group only, dev only: skip tax ID / DOB and send placeholders. */
  testDummyValues?: boolean;
  /** Collect Group only: the APM scheme slugs to offer on checkout for this mapping. Omit (or send
   * undefined) to leave unchanged; an empty array clears the selection. */
  apmSchemes?: string[];
}) {
  return apiService.post(API_PATHS.UPDATE_TERMINAL_MAPPING, payload);
}

export type ApmSchemeCatalogEntry = { slug: string; label: string; currency: string };

/** The fixed catalog of selectable APM schemes (Collect Group mapping modal). Source of truth is
 * the backend ApmSchemeCatalog, so this never drifts from what checkout actually offers. */
export async function fetchApmSchemes(): Promise<ApmSchemeCatalogEntry[]> {
  const res: any = await apiService.get(API_PATHS.GET_APM_SCHEMES);
  const rows = (res?.data ?? res) as any[];
  if (!Array.isArray(rows)) return [];
  return rows
    .map((r) => ({
      slug: String(r?.slug ?? ''),
      label: String(r?.label ?? r?.slug ?? ''),
      currency: String(r?.currency ?? ''),
    }))
    .filter((r) => r.slug.length > 0);
}

export async function updateMerchantPriorityRouting(merchantId: number, priorityRoutingEnabled: boolean) {
  await apiService.patch(getMerchantPriorityRoutingPath(merchantId), null, {
    params: { priorityRoutingEnabled },
  });
}

export async function deleteTerminalMapping(mappingId: number) {
  return apiService.patch(getTerminalMappingSoftDeletePath(mappingId));
}

export async function deleteMerchantTerminal(terminalId: number) {
  return apiService.patch(getMerchantTerminalSoftDeletePath(terminalId));
}

export async function deleteAcquirerTerminal(terminalId: number) {
  return apiService.patch(getAcquirerTerminalSoftDeletePath(terminalId));
}
